const WORLDPAY_LIBRARY_URL = 'https://payments.worldpay.com/resources/hpp/integrations/embedded/js/hpp-embedded-integration-library.js';
const SCRIPT_TIMEOUT_MS = 5000;
const HPP_READY_TIMEOUT_MS = 7000;

let libraryPromise = null;

export function isWorldpayCheckoutUrl(url) {
    try {
        const parsed = new URL(url, window.location.href);

        return /(^|\.)worldpay\.com$/i.test(parsed.hostname);
    } catch (e) {
        return false;
    }
}

export function extractWorldpayOrderNumber(url) {
    try {
        const params = new URL(url, window.location.href).searchParams;
        const resultUrlKeys = ['successURL', 'failureURL', 'pendingURL', 'cancelURL'];

        for (let i = 0; i < resultUrlKeys.length; i += 1) {
            const resultUrl = params.get(resultUrlKeys[i]);
            if (!resultUrl) {
                continue;
            }

            const productId = new URL(resultUrl, window.location.href).searchParams.get('productId');
            if (productId) {
                return productId;
            }
        }
    } catch (e) {
    }

    return '';
}

function hasWorldpayLibrary() {
    return Boolean(window.WPCL && typeof window.WPCL.Library === 'function');
}

function loadWorldpayLibrary() {
    if (hasWorldpayLibrary()) {
        return Promise.resolve();
    }

    if (libraryPromise) {
        return libraryPromise;
    }

    libraryPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${WORLDPAY_LIBRARY_URL}"]`);
        const script = existing || document.createElement('script');
        let settled = false;

        const finish = callback => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            callback();
        };

        const timer = setTimeout(() => {
            finish(() => reject(new Error('WorldPay embedded library load timeout')));
        }, SCRIPT_TIMEOUT_MS);

        script.onload = () => {
            finish(() => {
                if (hasWorldpayLibrary()) {
                    resolve();
                } else {
                    reject(new Error('WorldPay embedded library did not expose WPCL.Library'));
                }
            });
        };

        script.onerror = () => {
            finish(() => reject(new Error('WorldPay embedded library load failed')));
        };

        if (!existing) {
            script.src = WORLDPAY_LIBRARY_URL;
            script.async = true;
            document.head.appendChild(script);
        }
    });

    return libraryPromise;
}

function createHiddenContainer() {
    const container = document.createElement('div');
    container.id = `worldpay-hpp-gate-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
    container.style.position = 'fixed';
    container.style.left = '-10000px';
    container.style.top = '0';
    container.style.width = '1px';
    container.style.height = '1px';
    container.style.opacity = '0';
    container.style.overflow = 'hidden';
    container.style.pointerEvents = 'none';
    document.body.appendChild(container);

    return container;
}

export function prepareWorldpayRedirect(url, timeoutMs = HPP_READY_TIMEOUT_MS) {
    if (!isWorldpayCheckoutUrl(url)) {
        return Promise.resolve(true);
    }

    return loadWorldpayLibrary().then(() => new Promise(resolve => {
        const container = createHiddenContainer();
        const libraryObject = new window.WPCL.Library();
        let settled = false;

        const cleanup = () => {
            clearTimeout(timer);
            try {
                libraryObject.destroy();
            } catch (e) {
            }
            if (container.parentNode) {
                container.parentNode.removeChild(container);
            }
        };

        const finish = ready => {
            if (settled) {
                return;
            }
            settled = true;
            if (ready) {
                clearTimeout(timer);
            } else {
                cleanup();
            }
            resolve(ready);
        };

        const timer = setTimeout(() => finish(false), timeoutMs);

        try {
            libraryObject.setup({
                url,
                type: 'iframe',
                inject: 'immediate',
                target: container.id,
                hideContent: true,
                accessibility: false,
                debug: false,
                flowCallback: () => finish(true),
                resultCallback: result => {
                    const status = result && result.order && result.order.status;
                    if (['error', 'exception', 'session_expired'].indexOf(status) !== -1) {
                        finish(false);
                    }
                },
            });
        } catch (e) {
            finish(false);
        }
    })).catch(() => false);
}

export function redirectViaWorldpayGate(url, {
    orderNumber = '',
    fallbackUrl = '',
    headers = {},
    timeout = 60000,
    onFailure = () => {},
} = {}) {
    if (!isWorldpayCheckoutUrl(url)) {
        window.location.href = url;
        return Promise.resolve(true);
    }

    return prepareWorldpayRedirect(url).then(ready => {
        if (ready) {
            window.location.href = url;
            return true;
        }

        const resolvedOrderNumber = orderNumber || extractWorldpayOrderNumber(url);
        const resolvedFallbackUrl = fallbackUrl || (resolvedOrderNumber
            ? `/api/orders/${resolvedOrderNumber}/pay-fallback?worldpayReachable=0`
            : '');

        if (!resolvedFallbackUrl) {
            onFailure();
            return false;
        }

        return fetch(resolvedFallbackUrl, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({}),
            signal: window.AbortSignal && window.AbortSignal.timeout
                ? window.AbortSignal.timeout(timeout)
                : undefined,
        })
            .then(response => (response.ok ? response.json() : null))
            .then(data => {
                const nextUrl = typeof data === 'string' ? data : data && data.url;
                if (nextUrl && !isWorldpayCheckoutUrl(nextUrl)) {
                    window.location.href = nextUrl;
                    return true;
                }

                onFailure();
                return false;
            })
            .catch(() => {
                onFailure();
                return false;
            });
    });
}
